import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import * as Icons from "lucide-react";
import { useLang } from "@/contexts/LangContext";
import ServiceVisual from "./ServiceVisuals";

const ease = [0.22, 1, 0.36, 1];

export default function Services() {
  const { t } = useLang();
  const items = t.services.items;
  const sectionRef = useRef(null);

  // Soft parallax — each column drifts at a different speed
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });
  const y0 = useTransform(scrollYProgress, [0, 1], [20, -20]);
  const y1 = useTransform(scrollYProgress, [0, 1], [52, -52]);
  const y2 = useTransform(scrollYProgress, [0, 1], [34, -34]);
  const colY = [y0, y1, y2];

  return (
    <section
      ref={sectionRef}
      id="services"
      data-testid="services-section"
      className="relative py-28 lg:py-36 overflow-hidden"
    >
      <div className="absolute inset-0 bg-radial-blue opacity-50 pointer-events-none" />
      <div className="mx-auto max-w-7xl px-5 relative">
        <SectionHeader t={t} />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
          {items.map((s, i) => (
            <motion.div key={s.title} style={{ y: colY[i % 3] }} className="h-full">
              <ServiceCard
                service={s}
                index={i}
                isHighlight={i === 1}
                exploreLabel={t.services.explore}
              />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function SectionHeader({ t }) {
  return (
    <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 mb-14">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7, ease }}
        className="lg:col-span-6"
      >
        <div className="text-[11px] sm:text-xs uppercase tracking-[0.24em] text-brand-blue font-mono-tight mb-4">
          {t.services.eyebrow}
        </div>
        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-white leading-[1.02]">
          {t.services.title} <br />
          <span className="text-gradient-brand">{t.services.title2}</span>
        </h2>
      </motion.div>
      <motion.p
        initial={{ opacity: 0, y: 18 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7, ease, delay: 0.1 }}
        className="lg:col-span-5 lg:col-start-8 text-white/65 leading-relaxed text-base lg:text-lg max-w-md self-end"
      >
        {t.services.subtitle}
      </motion.p>
    </div>
  );
}

function ServiceCard({ service, index, isHighlight, exploreLabel }) {
  const Icon = Icons[service.icon] ?? Icons.Sparkles;
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.65, ease, delay: index * 0.06 }}
      whileHover={{ y: -6 }}
      data-testid={`service-card-${index}`}
      className={`group relative h-full rounded-3xl p-6 lg:p-7 overflow-hidden transition-all duration-300 ${
        isHighlight ? "glass-strong ring-1 ring-brand-blue/30" : "glass hover:border-white/20"
      }`}
    >
      {/* hover glow */}
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
        <div className="absolute -top-20 -right-20 h-60 w-60 rounded-full bg-brand-blue/15 blur-3xl" />
        <div className="absolute -bottom-20 -left-10 h-48 w-48 rounded-full bg-brand-orange/10 blur-3xl" />
      </div>

      <div className="relative">
        {/* animated visual mockup */}
        <div className="relative mb-6 transition-transform duration-500 group-hover:scale-[1.015]">
          <ServiceVisual iconName={service.icon} />
          {/* glass overlay on hover */}
          <div className="pointer-events-none absolute inset-0 rounded-2xl bg-gradient-to-t from-[#0b1220] via-transparent to-transparent opacity-60 group-hover:opacity-30 transition-opacity duration-500" />
        </div>

        <div className="flex items-start justify-between mb-5">
          <div
            className={`h-11 w-11 grid place-items-center rounded-2xl border border-white/10 transition-transform duration-300 group-hover:scale-110 group-hover:-rotate-3 ${
              isHighlight ? "bg-gradient-to-br from-brand-blue/30 to-brand-blue/5" : "bg-white/[0.04]"
            }`}
          >
            <Icon
              className={`h-5 w-5 ${isHighlight ? "text-brand-blue" : "text-white"}`}
              strokeWidth={1.6}
            />
          </div>
          <span className="text-[10.5px] uppercase tracking-[0.2em] text-white/45 font-mono-tight">
            {service.tag}
          </span>
        </div>

        <h3 className="font-display text-xl sm:text-[21px] lg:text-[22px] font-bold tracking-tight text-white leading-tight">
          {service.title}
        </h3>
        <p className="mt-3 text-[14.5px] sm:text-[15px] lg:text-base text-white/60 leading-relaxed">{service.desc}</p>

        <div className="mt-6 flex items-center gap-1.5 text-[12.5px] font-medium text-white/55 group-hover:text-white transition">
          <span>→</span>
          <span className="opacity-0 group-hover:opacity-100 translate-x-[-6px] group-hover:translate-x-0 transition-all">
            {exploreLabel}
          </span>
        </div>
      </div>
    </motion.div>
  );
}
